import React, {useMemo} from "react";
import {Link, Stack} from "@mui/material";
import {Link as RouterLink} from "react-router";
import {useAssignedDeskStore} from "../../../api/AssignedDeskStore";
import {useEmployeeVisitStore} from "../../../api/EmployeeVisitStore";
import {useNeighbourhoodStore} from "../../../api/NeighbourhoodStore";
import {AppPages, usePageUrl} from "../../../app-layout/AppPages";
import {DateRangeOptions} from "../../../components/filter-fields/DateRangeFilter";
import NeighbourhoodColorIcon from "../../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {CsvModel} from "../../../util/CSVWriteUtil";
import {RemoteValue} from "../../../util/RemoteValue";
import {
    Desks_Read,
    Employees_Read,
    KeyData_Read,
    Neighbourhoods_Read,
    NewJoiners_Read,
    VisitReports_Read,
} from "../../../util/access/Permission";
import {AbandonedNeighbourhood, findAbandonedNeighbourhoods} from "./AbandonedNeighbourhoods";
import {DashboardWidget, DashboardWidgetItem} from "../DashboardWidget";
import {WidgetFigure, WidgetStats} from "../WidgetFigure";

const csvModel = RemoteValue.fromValue(new CsvModel<AbandonedNeighbourhood>([
    {header: 'Neighbourhood', value: ({neighbourhood}) => neighbourhood.name},
    {header: '100% median visits', value: ({actual}) => actual.median.value.toString()},
    {header: '100% median visits %', value: ({actual}) => Number.isFinite(actual.median.percent) ? `${actual.median.percent}%` : ''},
]));

export function AbandonedNeighbourhoodsWidget() {
    const pageUrl = usePageUrl();
    const neighbourhoods = useNeighbourhoodStore(state => state.remoteValue);
    const employeeVisits = useEmployeeVisitStore(state => state.remoteValue);
    const assignedDesks = useAssignedDeskStore(state => state.remoteValue);
    const problems = useMemo(() =>
        RemoteValue.combine({employeeVisits, assignedDesks, neighbourhoods})
            .map(values => findAbandonedNeighbourhoods(
                values.employeeVisits,
                values.assignedDesks,
                values.neighbourhoods,
                DateRangeOptions.lastThreeMonths,
            )),
        [employeeVisits, assignedDesks, neighbourhoods],
    );

    return (
        <DashboardWidget
            title="Abandoned neighbourhoods"
            description="Neighbourhoods whose actual median attendance is at most 40% with the 100% desk-assignment threshold over the last three months."
            problems={problems}
            requiredPermissions={[
                Desks_Read,
                Employees_Read,
                KeyData_Read,
                Neighbourhoods_Read,
                NewJoiners_Read,
                VisitReports_Read,
            ]}
            csvModel={csvModel}
            renderProblem={({neighbourhood, actual}, index) => (
                <DashboardWidgetItem
                    key={neighbourhood.id}
                    index={index}
                    left={
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                            <Link component={RouterLink} to={pageUrl(AppPages.neighbourhoodVisitReport, undefined, {search: neighbourhood.name})} underline="hover">
                                {neighbourhood.name}
                            </Link>
                        </Stack>
                    }
                    right={
                        <>
                            <WidgetFigure label="Desks" figure={neighbourhood.desks.length}/>
                            <WidgetFigure label="Visit median" figure={<WidgetStats figure={actual.median}/>}/>
                        </>
                    }
                />
            )}
        />
    );
}
